import React, { useEffect, useState } from 'react';

/**
 * Enhanced CircularProgress component with support for:
 * - Values over 100% (overlapping rings)
 * - Color transitions as progress increases
 * - Rainbow animation when exceeding bonus threshold
 */
function CircularProgress({ 
  percentage = 0, 
  size = 64, 
  strokeWidth = 8, 
  baseColor = 'var(--border-color)',
  regularColor = '#3B82F6', 
  approachingColor = 'var(--success-color)', 
  exceedingColor = '#F59E0B',
  bonusThreshold = 120,
  showAnimation = true
}) {
  // Ensure percentage is a number and not less than 0
  const numericPercentage = Math.max(0, Number(percentage) || 0);
  
  // State
  const [rainbowAngle, setRainbowAngle] = useState(0);
  
  // Calculate dimensions
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  
  // Determine if we should show the rainbow effect
  const isRainbow = showAnimation && numericPercentage >= bonusThreshold;
  
  // For percentages > 100, we calculate how many full circles to show
  const completedCircles = Math.floor(numericPercentage / 100);
  
  // Normalize percentage for the current progress circle (0-100)
  const normalizedPercentage = numericPercentage % 100 || (numericPercentage >= 100 ? 100 : numericPercentage);
  
  // Calculate offset for the current progress circle
  const currentOffset = circumference - (normalizedPercentage / 100) * circumference;
  
  // Determine color based on percentage
  let currentColor = regularColor;
  if (numericPercentage >= 100) {
    currentColor = exceedingColor;
  } else if (numericPercentage >= 80) {
    currentColor = approachingColor;
  }
  
  // Rainbow animation effect
  useEffect(() => {
    let animationFrame;
    
    if (isRainbow) {
      const animate = () => {
        setRainbowAngle(prev => (prev + 1) % 360);
        animationFrame = requestAnimationFrame(animate);
      };
      
      animationFrame = requestAnimationFrame(animate);
    }
    
    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [isRainbow]);
  
  // Glow effect
  const glowEffect = numericPercentage >= 80 ? {
    filter: `drop-shadow(0 0 ${Math.min((numericPercentage - 80) / 5, 6)}px ${currentColor})`
  } : {};
  
  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg 
        width={size} 
        height={size} 
        viewBox={`0 0 ${size} ${size}`}
        style={glowEffect}
      >
        {/* Rainbow gradient definition */}
        {isRainbow && (
          <defs>
            <linearGradient id={`rainbow-${size}`} x1="0%" y1="0%" x2="100%" y2="0%" gradientTransform={`rotate(${rainbowAngle})`}>
              <stop offset="0%" stopColor="#FF0000" />
              <stop offset="16.67%" stopColor="#FF8000" />
              <stop offset="33.33%" stopColor="#FFFF00" />
              <stop offset="50%" stopColor="#00FF00" />
              <stop offset="66.67%" stopColor="#0080FF" />
              <stop offset="83.33%" stopColor="#8000FF" />
              <stop offset="100%" stopColor="#FF0080" />
            </linearGradient>
          </defs>
        )}
        
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={baseColor}
          strokeWidth={strokeWidth}
        />
        
        {/* Completed circles (for values > 100%) */}
        {Array.from({ length: completedCircles }).map((_, index) => (
          <React.Fragment key={`complete-${index}`}>
            {/* Shadow/outline */}
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke="rgba(0,0,0,0.15)"
              strokeWidth={strokeWidth + 2}
              strokeDasharray={circumference}
              strokeDashoffset={0}
              strokeLinecap="round"
              transform={`rotate(-90 ${size/2} ${size/2})`}
            />
            
            {/* Filled circle */}
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke={isRainbow ? `url(#rainbow-${size})` : currentColor}
              strokeWidth={strokeWidth}
              strokeDasharray={circumference}
              strokeDashoffset={0}
              strokeLinecap="round"
              transform={`rotate(-90 ${size/2} ${size/2})`}
              style={{ opacity: 0.9 - (index * 0.1) }}
            />
          </React.Fragment>
        ))}
        
        {/* Current progress - shadow/outline */}
        {normalizedPercentage > 0 && (
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="rgba(0,0,0,0.15)"
            strokeWidth={strokeWidth + 2}
            strokeDasharray={circumference}
            strokeDashoffset={currentOffset}
            strokeLinecap="round"
            transform={`rotate(-90 ${size/2} ${size/2})`}
          />
        )}
        
        {/* Current progress circle */}
        {normalizedPercentage > 0 && (
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={isRainbow ? `url(#rainbow-${size})` : currentColor}
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={currentOffset}
            strokeLinecap="round"
            transform={`rotate(-90 ${size/2} ${size/2})`}
          />
        )}
      </svg>
      
      {/* Percentage text */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column'
      }}>
        <span style={{
          fontSize: `${size / 4}px`,
          fontWeight: 'bold',
          color: isRainbow ? '#FFFFFF' : '#333333',
          textShadow: isRainbow ? `0 0 5px ${currentColor}` : 'none'
        }}>
          {Math.round(numericPercentage)}%
        </span>
        
        {numericPercentage > 100 && size >= 80 && (
          <span style={{
            fontSize: `${size / 12}px`,
            color: isRainbow ? '#FFFFFF' : '#666666',
            marginTop: `-${size / 20}px`,
            textShadow: isRainbow ? `0 0 3px ${currentColor}` : 'none'
          }}>
            {completedCircles}x+{Math.round(normalizedPercentage)}%
          </span>
        )}
      </div>
    </div>
  );
}

export default CircularProgress;